import { useState, useEffect } from "react";
import { getCreditMetrics } from "../api";

function fmt(n) { return n?.toLocaleString() ?? "—"; }
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const IMP_BG    = (s) => s <= 3 ? "var(--success-light, #f0fdf4)" : s <= 6 ? "var(--warning-light, #fef3c7)" : "var(--danger-light, #fee2e2)";
const IMP_COLOR = (s) => s <= 3 ? "var(--success, #15803d)"       : s <= 6 ? "var(--warning, #b45309)"       : "var(--danger, #b91c1c)";

const STATUS_MAP = {
  pending:          { label: "Pending",          cls: "badge-pending"  },
  manager_approved: { label: "Manager Approved", cls: "badge-medium"   },
  approved:         { label: "Approved",         cls: "badge-approved" },
  rejected:         { label: "Rejected",         cls: "badge-rejected" },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status, cls: "" };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

function StatTile({ label, value, sub, valueStyle }) {
  return (
    <div className="stat-tile">
      <div className="label">{label}</div>
      <div className="value" style={valueStyle}>{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

export default function CreditsDashboard({ auth, onNavigate }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // Managers only ever see their own + their reportees' credit requests here;
  // admins get the org-wide view (no filter) — same scoping split as the
  // Credit Requests list page.
  const isManager = auth?.role === "manager";

  useEffect(() => {
    getCreditMetrics(isManager ? { manager_email: auth.email } : {})
      .then(setMetrics)
      .catch((err) => setError(err.message || "Failed to load metrics"))
      .finally(() => setLoading(false));
  }, [isManager, auth?.email]);

  if (loading) return (
    <div>
      <div className="page-header"><h1>Credits Dashboard</h1></div>
      <div className="page-body text-muted">Loading…</div>
    </div>
  );

  if (error) return (
    <div>
      <div className="page-header"><h1>Credits Dashboard</h1></div>
      <div className="page-body error-msg">{error}</div>
    </div>
  );

  const m = metrics;
  const maxTeamCount = Math.max(...(m.by_team.map((t) => t.count) || [1]), 1);

  return (
    <div>
      <div className="page-header">
        <h1>Credits Dashboard</h1>
        <p>{isManager ? "Overview of credit requests from you and your reports." : "Overview of Claude credit requests across your organisation."}</p>
      </div>

      <div className="page-body">

        {/* ── Stat tiles ── */}
        <div className="stat-grid">
          <StatTile label="Total Requests"     value={m.total} />
          <StatTile label="Pending Review"     value={m.pending}
            valueStyle={{ color: "var(--warning, #b45309)" }} />
          <StatTile label="At Final Approval"  value={m.at_admin}
            valueStyle={{ color: "var(--primary, #2563eb)" }} />
          <StatTile label="Approved"           value={m.approved}
            valueStyle={{ color: "var(--success, #15803d)" }} />
          <StatTile label="Rejected"           value={m.rejected}
            valueStyle={{ color: "var(--danger, #b91c1c)" }} />
          <StatTile label="Avg Importance"     value={`${m.avg_importance} / 10`} />
          <StatTile label="Credits Requested"  value={fmt(Math.round(m.total_credits_requested))}
            sub="Anthropic units" />
          <StatTile label="Credits Approved"   value={fmt(Math.round(m.total_credits_approved))}
            sub="Anthropic units" valueStyle={{ color: "var(--success, #15803d)" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

          {/* ── Requests by team ── */}
          <div className="card">
            <div className="card-header">Requests by Team</div>
            <div className="card-body">
              {m.by_team.length === 0 ? (
                <div className="text-muted">No data yet.</div>
              ) : (
                <div className="bar-chart">
                  {m.by_team.map((row) => (
                    <div key={row.team} className="bar-row">
                      <div className="bar-label">{row.team}</div>
                      <div className="bar-track">
                        <div className="bar-fill"
                          style={{ width: `${(row.count / maxTeamCount) * 100}%` }} />
                      </div>
                      <div className="bar-value">{row.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Status breakdown ── */}
          <div className="card">
            <div className="card-header">Status Breakdown</div>
            <div className="card-body">
              <div className="bar-chart">
                {[
                  { label: "Pending",          value: m.pending,  color: "var(--warning, #b45309)" },
                  { label: "Manager Approved", value: m.at_admin, color: "var(--primary, #2563eb)" },
                  { label: "Approved",         value: m.approved, color: "var(--success, #15803d)" },
                  { label: "Rejected",         value: m.rejected, color: "var(--danger, #b91c1c)"  },
                ].map((row) => (
                  <div key={row.label} className="bar-row">
                    <div className="bar-label">{row.label}</div>
                    <div className="bar-track">
                      <div className="bar-fill"
                        style={{ width: m.total ? `${(row.value / m.total) * 100}%` : "0%",
                                 background: row.color }} />
                    </div>
                    <div className="bar-value">{row.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── High-priority pending requests ── */}
        {(m.top_pending || []).length > 0 && (
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-header">
              High-Priority Pending Requests
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Requester</th>
                    <th>Team</th>
                    <th>Credits</th>
                    <th>Importance</th>
                    <th>Status</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {m.top_pending.map((req) => (
                    <tr key={req.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => onNavigate?.("creditrequests", { status: req.status })}
                    >
                      <td style={{ fontWeight: 600 }}>{req.project_name}</td>
                      <td className="text-muted">{req.requester_name}</td>
                      <td className="text-muted">{req.team || "—"}</td>
                      <td style={{ fontFamily: "monospace" }}>{fmt(req.credits_requested)}</td>
                      <td>
                        <span className="badge" style={{ background: IMP_BG(req.importance_score), color: IMP_COLOR(req.importance_score) }}>
                          {req.importance_score}/10
                        </span>
                      </td>
                      <td><StatusBadge status={req.status} /></td>
                      <td className="text-muted">{fmtDate(req.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(m.top_pending || []).length === 0 && m.total > 0 && (
          <div className="card card-body" style={{ marginTop: 24, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
            <div className="text-muted">No pending requests — all caught up!</div>
          </div>
        )}

      </div>
    </div>
  );
}
